(() => {
  // Support both old and new option names during transition
  const CUTOVER_VARIANT_OPTIONS = [
    'Hidden: Cutover Variant',        // Legacy name (being phased out)
    'Choose from available inventory', // New name for Shop App compatibility
    'Choose from available',           // Actual option name in use
  ];
  // Variants with custom.cutover_sku=true are only sellable on/after this date.
  // Variants without the metafield are only sellable before this date.
  const CUTOVER_DATE = new Date('2026-01-05T12:00:00Z');

  // Returns the cutover date (hardcoded for production)
  const cutover = () => CUTOVER_DATE;

  /**
   * Check if a location is retail-only (not for online fulfillment)
   */
  const isRetailLocation = (name) => {
    if (!name) return false;
    const lower = name.toLowerCase();
    return lower.includes('cabana') || lower.includes('popup');
  };

  const STOREFRONT_TOKENS = {
    'goodr-test-store.myshopify.com': '8c6d096e6173bff340b3c06d21014978',
    'goodr-test-store-2.myshopify.com': 'e9928111cd3c4a946e27644402a88c42',
    'goodr-sunglasses.myshopify.com': '49dee85da49072f2a18256fcc617c90e',
    'goodr-canada-d2c.myshopify.com': '2c3fae8a0eb79f63509decfddac06d3f',
  };

  const cache = new Map();
  let batchFetchInProgress = false;

  const getShopDomain = () => (window.Shopify && window.Shopify.shop) || '';

  const resolveStorefrontToken = () => {
    const domain = getShopDomain();
    return (
      STOREFRONT_TOKENS[domain] ||
      STOREFRONT_TOKENS['goodr-sunglasses.myshopify.com']
    );
  };

  /**
   * Query a single variant's location-level inventory
   * This is the ONLY way to get per-location data via Storefront API
   * Falls back to quantityAvailable when storeAvailability doesn't return warehouse data
   */
  const getVariantLocationInventory = async (variantId, token) => {
    const query = `
      query getVariantLocations($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            quantityAvailable
            storeAvailability(first: 100) {
              edges {
                node {
                  quantityAvailable
                  location {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch('/api/2025-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: { id: variantId },
        }),
      });

      const payload = await response.json();

      if (!payload.data?.node) {
        console.warn('[Cutover] Variant not found:', variantId);
        return { quantity: 0, locations: [] };
      }

      const stores = payload.data.node.storeAvailability?.edges || [];

      const locations = stores.map(store => ({
        name: store?.node?.location?.name || 'Unknown',
        qty: store?.node?.quantityAvailable ?? 0,
        isRetail: isRetailLocation(store?.node?.location?.name || ''),
      }));

      // Filter out retail locations
      const nonRetailStores = stores.filter(store => {
        const name = store?.node?.location?.name || '';
        return !isRetailLocation(name);
      });

      // If we have non-retail pickup locations, sum them
      if (nonRetailStores.length > 0) {
        const quantity = nonRetailStores.reduce((sum, loc) => {
          const qty = loc?.node?.quantityAvailable ?? 0;
          return sum + qty;
        }, 0);
        return { quantity, locations };
      }

      // FALLBACK: If storeAvailability is empty or only has retail locations,
      // use quantityAvailable (includes all fulfillment locations)
      const totalAvailable = payload.data.node.quantityAvailable ?? 0;

      // If we have retail locations showing up, that means those ARE included in quantityAvailable
      // So we need to subtract them
      if (stores.length > 0) {
        const retailQuantity = stores.reduce((sum, loc) => {
          const name = loc?.node?.location?.name || '';
          const qty = loc?.node?.quantityAvailable ?? 0;
          return isRetailLocation(name) ? sum + qty : sum;
        }, 0);
        return { quantity: Math.max(0, totalAvailable - retailQuantity), locations };
      }

      // No location data at all - just use quantityAvailable
      return { quantity: totalAvailable, locations };

    } catch (error) {
      console.error('[Cutover] Location inventory fetch error', variantId, error);
      return { quantity: 0, locations: [] };
    }
  };

  const parseVariantEdge = (edge) => {
    const id = edge?.node?.id || '';
    const sku = edge?.node?.sku || '';
    const cutover = edge?.node?.cutoverSku?.value?.toLowerCase() === 'true';

    // Note: quantity will be populated by parallel location queries
    return {
      id: id.split('/').pop(),
      fullId: id,
      sku,
      quantity: 0, // Will be updated by getInventory
      cutover,
    };
  };

  const getInventory = async (productHandle) => {
    if (!productHandle) return [];

    // Check cache first (works for both batch and individual fetches)
    if (cache.has(productHandle)) {
      return cache.get(productHandle);
    }

    // Fall back to individual API call if not in cache (for dynamic loading)
    const token = resolveStorefrontToken();

    // Step 1: Get all variant IDs and basic info
    const query = `
      query getProductInventory($handle: String!) {
        product(handle: $handle) {
          variants(first: 20) {
            edges {
              node {
                id
                sku
                cutoverSku: metafield(namespace: "custom", key: "cutover_sku") {
                  value
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch('/api/2025-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query,
          variables: { handle: productHandle },
        }),
      });

      const payload = await response.json();
      const edges = payload?.data?.product?.variants?.edges || [];
      const parsed = edges.map(parseVariantEdge);

      // Step 2: Query each variant's location inventory in parallel
      const locationQueries = parsed.map(variant =>
        getVariantLocationInventory(variant.fullId, token)
      );

      const results = await Promise.all(locationQueries);

      // Step 3: Merge location quantities back into parsed variants
      const enriched = parsed.map((variant, i) => ({
        ...variant,
        quantity: results[i].quantity,
        _locations: results[i].locations,
      }));

      cache.set(productHandle, enriched);
      return enriched;
    } catch (error) {
      console.error('[Cutover] Inventory fetch error', error);
      cache.set(productHandle, []);
      return [];
    }
  };

  const findInventoryForVariant = (inventory, variantId) => {
    if (!variantId) return 0;
    const stringId = String(variantId);
    const match = inventory.find((entry) => String(entry.id) === stringId);
    return match?.quantity || 0;
  };

  const hasCutoverVariants = (optionNames = []) =>
    optionNames.some(
      (name) => typeof name === 'string' && CUTOVER_VARIANT_OPTIONS.includes(name),
    );

  const selectVariantByInventory = async (
    productVariants = [],
    productOptionNames = [],
    productHandle = '',
  ) => {
    if (!Array.isArray(productVariants) || productVariants.length === 0) {
      return { variant: null };
    }

    const cutoverVariants = hasCutoverVariants(productOptionNames);

    if (!cutoverVariants || productVariants.length === 1) {
      if (!productHandle) {
        return { variant: productVariants.find((v) => v.available !== false) || productVariants[0] };
      }

      const firstVariant = productVariants[0];
      let filteredQuantity;

      // Use GraphQL inventory (filtered by location)
      const inventory = await getInventory(productHandle);
      if (!inventory.length) {
        return { variant: productVariants.find((v) => v.available) || productVariants[0] || null };
      }
      const inventoryEntry = inventory.find((entry) => String(entry.id) === String(firstVariant.id));
      filteredQuantity = inventoryEntry?.quantity ?? firstVariant.inventory_quantity ?? 0;

      // Return enriched variant with filtered inventory
      const enrichedVariant = filteredQuantity > 0 ? {
        ...firstVariant,
        inventory: filteredQuantity,
        sku: firstVariant.sku || firstVariant.SKU || firstVariant.option1 || firstVariant.title || '',
      } : null;

      return { variant: enrichedVariant };
    }

    const inventory = await getInventory(productHandle);
    const enriched = productVariants.map((variant) => {
      const inventoryEntry = inventory.find((entry) => String(entry.id) === String(variant.id));
      const graphql = inventoryEntry?.quantity;
      const quantity = graphql ?? variant.inventory_quantity ?? 0;
      const source = graphql != null ? 'graphql' : (variant.inventory_quantity != null ? 'liquid' : 'default');

      if (source === 'liquid' && variant.inventory_quantity > 0) {
        console.warn('[Cutover] GraphQL miss for variant', variant.id, '— using Liquid inventory:', variant.inventory_quantity);
      }

      return {
        ...variant,
        inventory: quantity,
        _source: source,
        _locations: inventoryEntry?._locations || [],
        cutover: inventoryEntry?.cutover ?? variant.cutover ?? false,
        sku: variant.sku || inventoryEntry?.sku || variant.SKU || variant.option1 || variant.title || '',
      };
    });


    // Soft cutover: prefer old variants until depleted, then switch to new
    const today = new Date();
    const effectiveCutover = cutover();
    const afterCutover = today >= effectiveCutover;
    const old = enriched.filter(v => !v.cutover);
    const next = enriched.filter(v => v.cutover);
    const sellNew = afterCutover && !old.some(v => v.inventory > 0);
    const candidates = sellNew ? next : old;
    const available = candidates.filter(v => v.inventory > 0);

    // Find the variant we'll actually select (lowest stock among available candidates)
    const variant = available.length
      ? available.reduce((min, v) => (v.inventory < min.inventory ? v : min))
      : null;

    const oldStock = old.reduce((s, v) => s + v.inventory, 0);

    // Return variant + metadata for logging (logging moved to variants() helper)
    return {
      variant,
      enriched,
      sellNew,
      afterCutover,
      effectiveCutover,
      oldStock,
    };
  };

  const productCache = new Map();
  const pendingProducts = new Map();

  const normalizeHandle = (handle = '') => {
    if (!handle) return '';
    const sanitized = String(handle)
      .replace(/^https?:\/\/[^/]+\//i, '')
      .replace(/^products\//i, '')
      .replace(/\.js$/i, '')
      .split('?')[0]
      .trim();
    if (!sanitized) return '';
    return sanitized.charAt(0) === '/' ? sanitized.slice(1) : sanitized;
  };

  const getShopifyRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const fetchProductData = async (handle) => {
    const normalizedHandle = normalizeHandle(handle);
    if (!normalizedHandle) {
      console.warn('[Cutover] Missing product handle for fetchProductData');
      return null;
    }

    if (productCache.has(normalizedHandle)) {
      return productCache.get(normalizedHandle);
    }

    if (pendingProducts.has(normalizedHandle)) {
      return pendingProducts.get(normalizedHandle);
    }

    const endpoint = `${getShopifyRoot()}products/${normalizedHandle}.js`;
    const request = fetch(endpoint)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} for ${endpoint}`);
        }
        return response.json();
      })
      .then((product) => {
        productCache.set(normalizedHandle, product);
        pendingProducts.delete(normalizedHandle);
        return product;
      })
      .catch((error) => {
        console.error('[Cutover] Failed to fetch product data', {
          handle: normalizedHandle,
          endpoint,
          error,
        });
        pendingProducts.delete(normalizedHandle);
        return null;
      });

    pendingProducts.set(normalizedHandle, request);
    return request;
  };

  const selectVariant = async (handle) => {
    const product = await fetchProductData(handle);
    if (!product) {
      return { product: null, variant: null };
    }

    const optionNames = Array.isArray(product.options)
      ? product.options.map((option) => {
        if (typeof option === 'string') return option;
        if (option && typeof option.name === 'string') return option.name;
        return '';
      })
      : [];

    const variants = Array.isArray(product.variants) ? product.variants : [];

    const result = await selectVariantByInventory(
      variants,
      optionNames,
      product.handle || handle,
    );

    // Pass through enriched data for variants() debug logging
    return {
      product,
      variant: result.variant,
      enriched: result.enriched,
      sellNew: result.sellNew,
      afterCutover: result.afterCutover,
      effectiveCutover: result.effectiveCutover,
      oldStock: result.oldStock,
    };
  };

  const clearProductCache = () => {
    productCache.clear();
    pendingProducts.clear();
  };

  // Add multi-SKU product to cart with automatic variant fallback on quantity limit errors
  const add = async (handle, quantity = 1, variants = null, options = null) => {
    if (!handle) {
      throw new Error('Product handle is required');
    }

    let product = null;
    let variant = null;

    // Use provided data or fetch it
    if (!variants || !options) {
      const resolved = await selectVariant(handle);
      product = resolved.product;
      variant = resolved.variant;
      if (!product) {
        return { ok: false, soldOut: true };
      }
      variants = product.variants || [];
      options = Array.isArray(product.options)
        ? product.options.map((o) => (typeof o === 'string' ? o : o?.name || ''))
        : [];
    } else {
      // Caller provided variants/options, need to select
      const result = await selectVariantByInventory(variants, options, handle);
      variant = result.variant;
    }

    if (!variant) {
      return { ok: false, soldOut: true };
    }

    try {
      await window.addCartItem(variant.id, quantity);
      cache.clear();
      return { ok: true, variant };
    } catch (e) {
      const msg = (e?.message || '').toLowerCase();
      const isLimit =
        msg.includes('maximum quantity') || msg.includes('already in your cart');
      if (!isLimit) {
        throw e;
      }

      // Quantity limit hit - exclude failed variant, reselect, retry once
      cache.clear();
      const remainingVariants = variants.filter((v) => String(v.id) !== String(variant.id));
      const nextResult = await selectVariantByInventory(remainingVariants, options, handle);
      if (!nextResult.variant) {
        throw e;
      }

      console.info('[Cutover] Retrying with next variant', {
        from: variant.id,
        to: nextResult.variant.id,
      });
      await window.addCartItem(nextResult.variant.id, quantity);
      cache.clear();
      return { ok: true, variant: nextResult.variant };
    }
  };

  const getButtonsGroup = (button) => {
    if (!button) return [];
    const handle = button.getAttribute('data-cutover-product-handle');
    if (!handle) return [button];
    const container = button.closest('.goodr-product-grid-item-button_container');
    if (!container) return [button];
    return Array.from(
      container.querySelectorAll(`[data-cutover-atc][data-cutover-product-handle="${handle}"]`),
    );
  };

  const seedOriginalButtonState = (button) => {
    if (!button) return;
    if (!button.hasAttribute('data-cutover-original-html')) {
      button.setAttribute('data-cutover-original-html', button.innerHTML);
    }
    if (!button.hasAttribute('data-cutover-original-aria')) {
      const aria = button.getAttribute('aria-label');
      if (aria !== null) {
        button.setAttribute('data-cutover-original-aria', aria);
      } else {
        button.setAttribute('data-cutover-original-aria', '');
      }
    }
    if (!button.hasAttribute('data-cutover-initial-disabled')) {
      button.setAttribute('data-cutover-initial-disabled', button.disabled ? 'true' : 'false');
    }
  };

  const setButtonsLoading = (buttons, isLoading) => {
    buttons.forEach((button) => {
      if (!button) return;
      seedOriginalButtonState(button);
      if (isLoading) {
        button.dataset.cutoverLoading = 'true';
        button.disabled = true;
        button.classList.add('is-loading');
      } else {
        if (button.getAttribute('data-cutover-sold-out') === 'true') {
          return;
        }
        button.dataset.cutoverLoading = 'false';
        button.classList.remove('is-loading');
        if (button.getAttribute('data-cutover-initial-disabled') !== 'true') {
          button.disabled = false;
        }
        const originalHtml = button.getAttribute('data-cutover-original-html');
        if (originalHtml !== null) {
          button.innerHTML = originalHtml;
        }
        const originalAria = button.getAttribute('data-cutover-original-aria');
        if (originalAria !== null) {
          button.setAttribute('aria-label', originalAria);
        }
      }
    });
  };

  const markButtonsSoldOut = (buttons, productTitle = '') => {
    buttons.forEach((button) => {
      if (!button) return;

      // Skip if already marked as sold out (avoid unnecessary DOM mutations)
      if (button.dataset.cutoverSoldOut === 'true' &&
        button.disabled === true &&
        (!button.classList.contains('button-circle') ? button.innerHTML === 'SOLD OUT' : true)) {
        return;
      }

      seedOriginalButtonState(button);
      button.dataset.cutoverSoldOut = 'true';
      button.dataset.cutoverLoading = 'false';
      button.disabled = true;
      button.classList.remove('is-loading');
      if (!button.classList.contains('button-circle')) {
        button.innerHTML = 'SOLD OUT';
      }
      const ariaLabel = `SOLD OUT: ${productTitle || button.getAttribute('data-cutover-original-aria') || ''
        }`.trim();
      if (ariaLabel) {
        button.setAttribute('aria-label', ariaLabel);
      }
    });
  };

  // Extract product handle from button - works for both Liquid-rendered (data attribute) and Nosto-rendered (link href)
  const extractHandleFromButton = (button) => {
    // First try data attribute (Liquid-rendered)
    const dataHandle = button.getAttribute('data-cutover-product-handle');
    if (dataHandle) return dataHandle;

    // Fall back to extracting from parent link (Nosto-rendered)
    const link = button.closest('a[href*="/products/"]');
    if (link) {
      const href = link.getAttribute('href');
      const match = href.match(/\/products\/([^\/\?]+)/);
      if (match) return match[1];
    }

    return null;
  };

  const fetchAllProductsInventory = async () => {
    if (batchFetchInProgress) return;

    // Collect all unique product handles from buttons (works for both Liquid and Nosto-rendered)
    const buttons = document.querySelectorAll(
      '[data-cutover-product-handle], ' +
      '.goodr-product-grid-item-button, ' +
      'button[class*="button-bg-pink"]'
    );
    if (buttons.length === 0) return;

    const handles = new Set();
    buttons.forEach((button) => {
      const handle = extractHandleFromButton(button);
      if (handle && !cache.has(handle)) {
        handles.add(handle);
      }
    });

    if (handles.size === 0) return;

    batchFetchInProgress = true;
    const token = resolveStorefrontToken();
    const handlesArray = Array.from(handles);

    // Build query string for handles (Shopify query format: "handle:product1 OR handle:product2")
    // Escape handles to prevent query injection issues
    const handleQueries = handlesArray
      .map(handle => `handle:${handle.replace(/"/g, '\\"')}`)
      .join(' OR ');

    // Note: We can't query storeAvailability on variant lists (API limitation)
    // This batch query gets basic variant info, then we query locations individually
    const query = `
      query getProductsInventory {
        products(first: 50, query: "${handleQueries}") {
          edges {
            node {
              handle
              variants(first: 20) {
                edges {
                    node {
                      id
                      sku
                      cutoverSku: metafield(namespace: "custom", key: "cutover_sku") {
                        value
                      }
                    }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch('/api/2025-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();

      if (payload.errors) {
        console.error('[Cutover] GraphQL errors:', payload.errors);
        console.error('[Cutover] Query that failed:', query);
        // Fall back to individual queries if batch fails
        await Promise.all(
          handlesArray.map(handle => getInventory(handle))
        );
        return;
      }

      const products = payload?.data?.products?.edges || [];

      // Query location inventory for all variants in parallel
      const allVariantQueries = [];
      const productVariantMap = new Map(); // Track which product each variant belongs to

      products.forEach(({ node }) => {
        if (node && node.handle && node.variants) {
          const edges = node.variants.edges || [];
          const parsed = edges.map(parseVariantEdge);

          parsed.forEach(variant => {
            allVariantQueries.push(getVariantLocationInventory(variant.fullId, token));
            productVariantMap.set(variant.fullId, { handle: node.handle, variant });
          });
        }
      });

      // Execute all location queries in parallel
      const allResults = await Promise.all(allVariantQueries);

      // Group results by product handle
      const productInventoryMap = new Map();
      let queryIndex = 0;

      products.forEach(({ node }) => {
        if (node && node.handle && node.variants) {
          const edges = node.variants.edges || [];
          const parsed = edges.map(parseVariantEdge);

          const enriched = parsed.map(variant => {
            const result = allResults[queryIndex++] || { quantity: 0, locations: [] };
            return {
              ...variant,
              quantity: result.quantity,
              _locations: result.locations,
            };
          });

          productInventoryMap.set(node.handle, enriched);
        }
      });

      // Store in cache
      productInventoryMap.forEach((variants, handle) => {
        cache.set(handle, variants);
      });
    } catch (error) {
      console.error('[Cutover] Batch inventory fetch error, falling back to individual queries', error);
      // Fall back to individual queries if batch fails
      await Promise.all(
        handlesArray.map(handle => getInventory(handle))
      );
    } finally {
      batchFetchInProgress = false;
    }
  };

  const updateCollectionButtons = async () => {
    // Find all product grid item buttons (works for both Liquid and Nosto-rendered)
    const allButtons = document.querySelectorAll(
      '.goodr-product-grid-item-button, ' +
      'button[class*="button-bg-pink"], ' +
      '[data-cutover-product-handle]'
    );

    const handlesByProduct = new Map();

    // Group buttons by product handle
    allButtons.forEach((button) => {
      const handle = extractHandleFromButton(button);
      if (!handle) return;

      if (!handlesByProduct.has(handle)) {
        handlesByProduct.set(handle, []);
      }
      handlesByProduct.get(handle).push(button);
    });

    // Process all products in parallel for speed
    const productChecks = Array.from(handlesByProduct.entries()).map(async ([handle, productButtons]) => {
      // Fetch product data to get variants and options
      const product = await fetchProductData(handle);
      if (!product) return null;

      const variants = Array.isArray(product.variants) ? product.variants : [];

      // Bundle products are virtual - they don't have warehouse inventory
      // Trust Shopify's variant.available flag instead of querying API
      if (product.product_type === 'Bundle') {
        const available = variants.find(v => v.available !== false);

        if (available) return null; // Has stock - don't mark sold out

        // All variants sold out
        return {
          handle,
          product,
          variant: null,
          productButtons,
          title: product.title ||
            productButtons[0]?.getAttribute('data-cutover-product-title') ||
            productButtons[0]?.closest('.goodr-product-grid-item')?.querySelector('.goodr-product-grid-item-title')?.textContent?.trim() ||
            ''
        };
      }

      const optionNames = Array.isArray(product.options)
        ? product.options.map((option) => {
          if (typeof option === 'string') return option;
          if (option && typeof option.name === 'string') return option.name;
          return '';
        })
        : [];

      // Check inventory using cached data
      const inventoryResult = await selectVariantByInventory(
        variants,
        optionNames,
        handle,
      );

      // Return result for parallel processing
      return {
        handle,
        product,
        variant: inventoryResult.variant,
        productButtons,
        title: product.title ||
          productButtons[0]?.getAttribute('data-cutover-product-title') ||
          productButtons[0]?.closest('.goodr-product-grid-item')?.querySelector('.goodr-product-grid-item-title')?.textContent?.trim() ||
          ''
      };
    });

    const results = await Promise.all(productChecks);

    let soldOutCount = 0;
    results.forEach((result) => {
      if (!result || result.variant) return;
      soldOutCount++;
      const allButtonsForProduct = result.productButtons.flatMap(btn => getButtonsGroup(btn));
      markButtonsSoldOut(allButtonsForProduct.length > 0 ? allButtonsForProduct : result.productButtons, result.title);
    });
  };

  const handleCollectionCutoverClick = async (event) => {
    const button = event.target.closest('[data-cutover-atc]');
    if (!button) return;

    if (
      button.dataset.cutoverLoading === 'true' ||
      button.getAttribute('data-cutover-sold-out') === 'true'
    ) {
      event.preventDefault();
      return;
    }

    const handle = button.getAttribute('data-cutover-product-handle');
    if (!handle) return;

    event.preventDefault();

    const buttons = getButtonsGroup(button);
    setButtonsLoading(buttons, true);

    try {
      const quantityAttr = button.getAttribute('data-cutover-quantity');
      const quantity = quantityAttr ? Number(quantityAttr) || 1 : 1;

      const result = await add(handle, quantity);

      if (result.soldOut) {
        const title =
          button.getAttribute('data-cutover-product-title') || '';
        markButtonsSoldOut(buttons, title);
        return;
      }

      // Success - mark variant as resolved
      if (result.variant) {
        buttons.forEach((btn) => {
          btn.setAttribute('data-cutover-resolved-variant-id', result.variant.id);
        });
      }
    } catch (error) {
      console.error('[Cutover] Add to cart failed', {
        handle,
        error,
      });
    } finally {
      const enabledButtons = buttons.filter(
        (btn) => btn && btn.getAttribute('data-cutover-sold-out') !== 'true',
      );
      setButtonsLoading(enabledButtons, false);
    }
  };

  if (!window.__cutoverHandlerAttached) {
    document.addEventListener('click', handleCollectionCutoverClick);
    window.__cutoverHandlerAttached = true;
  }


  // Run batch inventory check on page load and pagination
  const runBatchInventoryCheck = async () => {
    const buttons = document.querySelectorAll(
      '[data-cutover-product-handle], ' +
      '.goodr-product-grid-item-button, ' +
      'button[class*="button-bg-pink"]'
    );

    if (buttons.length === 0) return;

    try {
      await fetchAllProductsInventory();
      await updateCollectionButtons();
    } catch (error) {
      console.error('[Cutover] Batch inventory check failed', error);
    }
  };

  // Run on page load (delay for Nosto to finish)
  const initInventoryCheck = () => {
    setTimeout(runBatchInventoryCheck, 1000);
  };

  // Run on pagination
  if (typeof jQuery !== 'undefined' && jQuery.fn && jQuery.fn.pjax) {
    jQuery(document).on('pjax:end', () => {
      setTimeout(runBatchInventoryCheck, 500);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInventoryCheck);
  } else {
    initInventoryCheck();
  }

  window.selectVariantByInventory = selectVariantByInventory;
  window.cutoverVariantSelection = Object.assign({}, window.cutoverVariantSelection, {
    selectVariantByInventory,
    fetchProductData,
    selectVariant,
    clearInventoryCache: () => cache.clear(),
    clearProductCache,
    handleCollectionCutoverClick,
    fetchAllProductsInventory,
    updateCollectionButtons,
    add,
    getInventory, // Debug helper
    getVariantLocationInventory, // Debug helper
  });

  window.variants = async ({ handle } = {}) => {
    const h = handle || location.pathname.split('/products/')[1]?.split(/[?#]/)[0];
    if (!h) return console.error('Not on a product page');

    // Use Liquid-rendered data if on current PDP (has real inventory for unlisted products)
    const pdp = window.pdpVariantData;
    const usePdpData = pdp && pdp.handle === h && pdp.variants?.length;

    let product, variant, enriched, sellNew, afterCutover, effectiveCutover, oldStock;

    if (usePdpData) {
      // Use Liquid data (includes inventory_quantity and cutover for unlisted products)
      product = await fetchProductData(h);
      const result = await selectVariantByInventory(pdp.variants, pdp.options, h);
      variant = result.variant;
      enriched = result.enriched;
      sellNew = result.sellNew;
      afterCutover = result.afterCutover;
      effectiveCutover = result.effectiveCutover;
      oldStock = result.oldStock;
    } else {
      // Fetch fresh data (works for listed products)
      const result = await selectVariant(h);
      product = result.product;
      variant = result.variant;
      enriched = result.enriched;
      sellNew = result.sellNew;
      afterCutover = result.afterCutover;
      effectiveCutover = result.effectiveCutover;
      oldStock = result.oldStock;
    }

    // Log cutover table for debugging
    if (enriched) {
      const cutoverStr = effectiveCutover.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

      console.log(`%c[Cutover] ${h}`, 'font-weight: bold');
      console.log(`→ Add to Bag: %c${variant?.sku || 'NONE'}%c (lowest stock)`, 'font-weight: bold', '');
      console.log(
        sellNew
          ? `→ Now selling new stock`
          : afterCutover
            ? `→ Old stock left: ${oldStock.toLocaleString()} — then new`
            : `→ Old stock left: ${oldStock.toLocaleString()} — then new (after ${cutoverStr})`
      );
      console.table(enriched.map(v => ({
        "SKU": v.sku,
        "Stock": v.inventory,
        "Source": v._source || "graphql",
        "Available": v.cutover
          ? (sellNew ? 'Now' : afterCutover ? 'After old stock sells out' : `After ${cutoverStr}`)
          : 'Now',
        "Variant Select": variant?.id === v.id ? "🟢" : null,
      })));

      // Location breakdown from enriched variant data (no global needed)
      console.log('%c[Location Inventory Breakdown]', 'font-weight: bold; color: #666');

      const totalInventory = enriched.reduce((sum, v) => sum + v.inventory, 0);
      let trackedLocationInventory = 0;
      let untrackedInventory = totalInventory;

      const locationMap = new Map();
      let hasAnyLocations = false;

      enriched.forEach(v => {
        (v._locations || []).forEach(loc => {
          hasAnyLocations = true;
          if (!locationMap.has(loc.name)) {
            locationMap.set(loc.name, { name: loc.name, qty: loc.qty, isRetail: loc.isRetail });
          } else {
            locationMap.get(loc.name).qty += loc.qty;
          }
        });
      });

      if (hasAnyLocations) {
        trackedLocationInventory = Array.from(locationMap.values()).reduce((sum, loc) => sum + loc.qty, 0);
        untrackedInventory = totalInventory - trackedLocationInventory;

        const locationData = {};
        let index = 0;

        if (untrackedInventory > 0) {
          locationData[index++] = {
            "Location": "📦 Warehouse/Online Inventory*",
            "Quantity": untrackedInventory,
            "Status": "✅ Counted"
          };
        }

        Array.from(locationMap.values())
          .sort((a, b) => {
            if (a.isRetail !== b.isRetail) return a.isRetail ? 1 : -1;
            return a.name.localeCompare(b.name);
          })
          .forEach(loc => {
            locationData[index++] = {
              "Location": loc.isRetail ? `🛍️ ${loc.name}` : `📦 ${loc.name}`,
              "Quantity": loc.qty,
              "Status": loc.isRetail ? "❌ Filtered" : "✅ Counted"
            };
          });

        console.table(locationData);

        const retailQty = Array.from(locationMap.values())
          .filter(loc => loc.isRetail)
          .reduce((sum, loc) => sum + loc.qty, 0);
        const warehouseQty = totalInventory - retailQty;

        console.log(`%c→ Total inventory: ${totalInventory} units`, 'color: #333');
        console.log(`%c→ Available for shipping: ${warehouseQty} units`, 'color: green');
        console.log(`%c→ Pickup-only (filtered): ${retailQty} units`, 'color: #ff6600');
        console.log(`%c* Warehouse inventory has no location tracking in Shopify`, 'color: #999; font-style: italic');
      } else {
        console.log('→ No location data available (Pickup in Store likely not enabled)');
        console.log(`→ All ${totalInventory} units available for shipping`);
      }

      if (!variant) {
        console.warn('[Cutover] No stock available for hidden variant product', {
          productHandle: h,
          variants: enriched.map(({ id, title, sku, inventory }) => ({ id, title, sku, inventory })),
        });
      }
    }

    return { product, variant };
  };
})();
